import './EditEvent.css'
import { ErrorAlert } from '../common/ErrorAlert';
import { useState, useMemo } from "react";
import { Button, DatePicker, Form, Input, Select, Spin, TimePicker, Popconfirm } from "antd";
import { Event, mapToEvent } from '../../models/calendar/Event.model';
import { useEvents } from '../../contexts/calendar/EventsContext.context';
import { useModalEvent } from '../../contexts/calendar/ModalEventContext.context';
import { EventService } from '../../services/events.api.client';
import { Dayjs } from 'dayjs';
import dayjs from '../../utils/dayjs'
import { EVENT_TYPE, statusOptions, typeOptions } from '../../constants/eventConstants';
import { useConfig } from '../../contexts/ConfigContext';
import { isOutsideCalendarHours } from '../../utils/calendarUtils';
import { useCompany } from '../../contexts/CompanyContext';
import { useCalendars } from '../../contexts/calendar/CalendarsContext.context';
import { useCalendar } from '../../contexts/calendar/CalendarContext.context';
import { useUser } from '../../contexts/UserContext';
import { Contact } from '../../services/contact.api.client';
import { ContactSearchSelect } from '../common/ContactSearchSelect';



type Props = {
    event:Event
}

type FieldType = {
    id: string;
    summary: string;
    start: Dayjs|string;
    status: string;
    type: string;
    uidCalendar: string;
    uidCompany: string;
    end: Dayjs|string;
    hour: string;
    minute: string;
    duration: Dayjs|string;
    responsibleId: string;
    participantIds: string[];
};

const optionsStatus = statusOptions;
const typeOptionsData = typeOptions;

export type UserKeyValue = {
    label:string
    key:string
}

const format = 'HH:mm';

export const EditEvent = ({ event }: Props)=>{
    const [currentEvent,setCurrentEvent] = useState<Event>(event);
    
    const endTime = dayjs(currentEvent.type == 'task'?currentEvent.start:currentEvent.end!).toDate();
    if(currentEvent.type == 'task'){
        endTime.setHours(0);
        endTime.setMinutes(currentEvent.duration);
    }

    const {calendar} = useCalendar();
    const {company} = useCompany();
    const [errorText,setErrorText] = useState<string>('');
    const [statusForm,setStatusForm] = useState<string>('SUCCESS');
    const [isTask,setIsTask] = useState<boolean>(currentEvent.type === EVENT_TYPE.TASK.dbValue);
    const {events, setEvents} = useEvents();
    const {setOptions} = useModalEvent();
    const [form] = Form.useForm<FieldType>();
    const {config} = useConfig();

    // Generate dynamic type options from CompanyContext
    const dynamicTypeOptions = useMemo(() => {
        let opts = company.moduleOptions?.filter((opt: any) => opt.type === 'EVENT_TYPE' && opt.show).map((opt: any) => ({
            label: opt.string_value,
            value: opt.key
        })) || [];
        
        // Fallback to constants if no dynamic options found
        if (opts.length === 0) {
            opts = typeOptionsData;
        }
        return opts;
    }, [company.moduleOptions]);

    const initialResponsible = useMemo(() => {
        if (event.responsible) return [event.responsible];
        return [];
    }, [event.responsible]);

    const initialParticipants = useMemo(() => {
        if (event.participants) return event.participants;
        return [];
    }, [event.participants]);

    const [contactMapping, setContactMapping] = useState<Record<string, Contact>>(() => {
        const mapping: Record<string, Contact> = {};
        if (event.responsible) mapping[event.responsible.uid] = event.responsible as unknown as Contact;
        if (event.participants) {
            event.participants.forEach(p => {
                mapping[p.uid] = p as unknown as Contact;
            });
        }
        return mapping;
    });

    const handleContactsFound = (contacts: Contact[]) => {
        setContactMapping(prev => {
            const next = { ...prev };
            contacts.forEach(c => {
                next[c.uid] = c;
            });
            return next;
        });
    };
    
    const handleClickDay = (isEditing:boolean,isViewing:boolean) =>{
        //console.log("evento ",event)
        const date = new Date(event.start);
        setOptions((prev)=>({
            ...prev,
            isopen:true,
            numberOfWeek:date.getDay(),
            day:date.getDate(),
            month:date.getMonth(),
            year:date.getFullYear(),
            id:event.id,
            isCreating:false,
            isEditing:isEditing,
            isViewing:isViewing
        }))
    }
    

    const { user: currentUser } = useUser();

    const handleSubmit = (dataForm:FieldType) => {
        if(config.key == null || config.key == ''){
            setErrorText('No se ha podido obtener la clave de seguridad, intente nuevamente.');
            return;
        }
        const dateStart = (dataForm.start as Dayjs).second(0).millisecond(0);
        
        let dateEnd: Dayjs;
        if (isTask) {
            if (dayjs.isDayjs(dataForm.duration)) {
                const durationMins = dataForm.duration.hour() * 60 + dataForm.duration.minute();
                dateEnd = dateStart.add(durationMins, 'minute');
            } else {
                dateEnd = dateStart; // fallback if somehow not a Dayjs
            }
        } else {
            dateEnd = (dataForm.end as Dayjs).second(0).millisecond(0);
        }

        if (calendar.weekAvailable && isOutsideCalendarHours(dateStart, dateEnd, calendar.weekAvailable)) {
            setErrorText('El horario seleccionado se encuentra fuera del horario configurado para el calendario.');
            return;
        }

        // Validación de solapamiento por responsable (tolerancia de 5 minutos)
        if (!isTask && dataForm.responsibleId) {
            const TOLERANCE_MS = 5 * 60 * 1000; // 5 minutos
            const newStart = dateStart.valueOf();
            const newEnd = dateEnd.valueOf();

            const conflict = events.find((ev) => {
                // Excluir el evento actual
                if (ev.uid === currentEvent.uid || ev.id === currentEvent.id) return false;
                if (ev.responsibleId !== dataForm.responsibleId) return false;
                if (!ev.start || !ev.end) return false;
                const evStart = new Date(ev.start).getTime();
                const evEnd = new Date(ev.end).getTime();
                const overlapStart = Math.max(newStart, evStart);
                const overlapEnd = Math.min(newEnd, evEnd);
                const overlapMs = overlapEnd - overlapStart;
                return overlapMs > TOLERANCE_MS;
            });

            if (conflict) {
                setErrorText(`El responsable ya tiene un evento programado ("${conflict.summary}") en ese horario. Se permite un solapamiento máximo de 5 minutos.`);
                return;
            }
        }

        const selectedResponsible = contactMapping[dataForm.responsibleId];
        const selectedParticipants = dataForm.participantIds?.map(uid => contactMapping[uid]).filter(Boolean);

        const data:Record<string, any> = {
            uid: currentEvent.uid,           // Firestore doc UID for the update
            uidCalendar: dataForm.uidCalendar || currentEvent.uidCalendar || calendar.uid,
            calendar_id: calendar.id, // Numeric ID from context
            company_id: company.current.id, // Numeric ID
            id: dataForm.id, 
            summary: dataForm.summary,
            start: dateStart.format('YYYY-MM-DDTHH:mm:ssZ'),
            end: isTask ? null : dateEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
            status: dataForm.status,
            type: dataForm.type,
            duration: isTask ? (dayjs.isDayjs(dataForm.duration) ? (dataForm.duration.hour() * 60 + dataForm.duration.minute()) : 0) : 0,
            createdBy: currentEvent.createdBy || currentUser.uid,
            responsible: selectedResponsible ? { id: selectedResponsible.id, uid: selectedResponsible.uid, name: selectedResponsible.name } : null,
            participants: selectedParticipants?.map(p => ({ id: p.id, uid: p.uid, name: p.name })),
            responsibleId: dataForm.responsibleId,
            participantIds: dataForm.participantIds,
            creator_enriched: currentEvent.creator_enriched
        };
        setStatusForm('LOAD');
        // Use company.current.uid directly — more reliable than form field which may be empty
        EventService.updateEvent(data, company.current.uid, dataForm.uidCalendar || currentEvent.uidCalendar || calendar.uid, config.key).then((response)=>{
            //console.log('Respuesta: '+response)
            const data: Event = mapToEvent(response) as Event;
            setCurrentEvent(data);
            setEvents((prevEvents:Event[])=>{
                const index = prevEvents.findIndex((event:Event)=> event.id == data.id || event.uid == data.uid);
                if(index !== -1){
                    const updatedEvents = [...prevEvents];
                    updatedEvents[index] = data;
                    return updatedEvents;
                }
                return prevEvents;
            });
            setStatusForm('SUCCESS');
            handleClickDay(false, true);
        }).catch((error_)=>{
            setErrorText(error_.message);
            setStatusForm('SUCCESS');
        })
    };

    const handleDelete = () => {
        setStatusForm('LOAD');
        EventService.deleteEvent(company.current.uid, currentEvent.uidCalendar || calendar.uid, currentEvent.id).then(()=>{
            setEvents((prevEvents:Event[])=>{
                return prevEvents.filter((event:Event)=>event.id !== currentEvent.id);
            });
            setStatusForm('SUCCESS');
            setOptions((prev)=>({
                ...prev,
                isopen:false,
                numberOfWeek:0,
                day:0,
                month:0,
                year:0,
                id:'',
                isCreating:false,
                isEditing:false,
                isViewing:false
            }))
        }).catch((error_)=>{
                console.log(error_);
                setErrorText('Error al eliminar el evento. Por favor, inténtelo de nuevo más tarde.');
                setStatusForm('SUCCESS');
            }
        );
    }
    /*<Spinner classNames={{label: "text-foreground mt-4"}} label="wave" variant="wave" /> */
    switch(statusForm){
        case 'LOAD':
            return(
                <div className="container-load" style={{width:'100%'}}>
                     <Spin size="large" />
                </div>
            );

        default:
            return (
                <>
                <div style={{paddingLeft:20,paddingRight:20}}>
                    <ErrorAlert message={errorText} onDismiss={() => setErrorText('')} />
                </div>
                <Form className="edit-form" style={{paddingLeft:20,paddingRight:20,paddingBottom:8}} onFinish={handleSubmit} form={form} layout="vertical"
                    initialValues={{
                        uidCalendar: currentEvent.uidCalendar || calendar.uid,
                        uidCompany: currentEvent.uidCompany,
                        id: currentEvent.id,
                        summary: currentEvent.summary,
                        duration:currentEvent.type == 'task'?dayjs(endTime):'', 
                        start: dayjs(currentEvent.start),
                        end: currentEvent.type == 'task' ? null : dayjs(currentEvent.end!),
                        status: currentEvent.status,
                        type: currentEvent.type,
                        responsibleId: currentEvent.responsibleId,
                        participantIds: currentEvent.participantIds
                    }}>
                    <a onClick={() => handleClickDay(false,true)} style={{margin:8,cursor:'pointer',display:'flex',alignItems:'center',color:'inherit',textDecoration:'none'}}>
                        <svg fill="#000000" viewBox="0 0 1024 1024" width={14} height={14} style={{marginRight:4}}>
                            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                            <g
                                id="SVGRepo_tracerCarrier"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            ></g>
                            <g id="SVGRepo_iconCarrier">
                                <path d="M222.927 580.115l301.354 328.512c24.354 28.708 20.825 71.724-7.883 96.078s-71.724 20.825-96.078-7.883L19.576 559.963a67.846 67.846 0 01-13.784-20.022 68.03 68.03 0 01-5.977-29.488l.001-.063a68.343 68.343 0 017.265-29.134 68.28 68.28 0 011.384-2.6 67.59 67.59 0 0110.102-13.687L429.966 21.113c25.592-27.611 68.721-29.247 96.331-3.656s29.247 68.721 3.656 96.331L224.088 443.784h730.46c37.647 0 68.166 30.519 68.166 68.166s-30.519 68.166-68.166 68.166H222.927z"></path>
                            </g>
                        </svg>
                        <span className="btn-back">Volver</span>
                    </a>
                    
                    <Form.Item label={null} hidden={true} name="id">
                        <input type="hidden" name="id" />
                    </Form.Item>
                    <Form.Item label={null} hidden={true} name="uidCalendar">
                            <input type="hidden" name="uidCalendar"/>
                    </Form.Item>
                    <Form.Item label={null} hidden={true} name="uidCompany">
                        <input type="hidden" name="uidCompany"/>
                    </Form.Item>
        
                    <Form.Item<FieldType>
                        label="Titulo"
                        name="summary"
                        rules={[{ required: true, message: 'Ingrese un titulo para el evento!' }]}
                        style={{width:'100%',marginBottom:8}}
                    >
                        <Input type="text" name="summary"/>
                    </Form.Item>

                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,width:'100%'}}>
                        <Form.Item<FieldType>
                            label="Inicio"
                            name="start"
                            rules={[{ required: true, message: 'Ingrese un fecha de inicio para el evento!' }]}
                            style={{width:'100%',marginBottom:0}}
                        >
                            <DatePicker
                                style={{width:'100%'}}
                                showTime={{ format: 'HH:mm', minuteStep: 10 }}
                                format="YYYY-MM-DD HH:mm"
                                needConfirm={false}
                            />
                        </Form.Item> 
                        {
                            isTask?
                            <Form.Item<FieldType>
                                label="Duración"
                                name="duration"
                                rules={[{ required: true, message: 'Ingrese una duración para la tarea!' }]}
                                style={{width:'100%',marginBottom:0}}
                            >
                                <TimePicker format={format} showNow={false} minuteStep={10} changeOnScroll/>
                            </Form.Item>
                            :
                            <Form.Item<FieldType>
                                label="Fin"
                                name="end"
                                rules={[{ required: true, message: 'Ingrese un fecha de fin para el evento!' }]}
                                style={{width:'100%',marginBottom:0}}
                            >
                                <DatePicker
                                    style={{width:'100%'}}
                                    showTime={{ format: 'HH:mm', minuteStep: 10 }}
                                    format="YYYY-MM-DD HH:mm"
                                    needConfirm={false}
                                />
                            </Form.Item>
                        }
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,width:'100%',marginTop:12}}>
                        <Form.Item<FieldType>
                            label="Estado"
                            name="status"
                            rules={[{ required: true, message: 'Ingrese un estado para el evento!' }]}
                            className="space-y-2"
                        >
                            <Select
                                className="max-w-xs"
                                placeholder="Seleccione un estado"
                            >
                                {
                                    optionsStatus.map((option) => (
                                        <Select.Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Select.Option>
                                    ))
                                }
                            </Select>
                        </Form.Item>   
                        <Form.Item<FieldType>
                            label="Tipo de Evento"
                            name="type"
                            rules={[{ required: true, message: 'Ingrese un tipo de evento!' }]}
                            className="space-y-2"
                        >
                            <Select
                                className="max-w-xs"
                                disabled
                                onChange={(option:string) => {
                                        if (option === EVENT_TYPE.TASK.dbValue) {
                                            setIsTask(true);
                                        } else {
                                            setIsTask(false);
                                        }
                                    }
                                }
                                placeholder="Seleccione un tipo"
                                >
                                    {
                                        dynamicTypeOptions.map((option:any) => (
                                            <Select.Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Select.Option>
                                        ))
                                    }
                            </Select>
                        </Form.Item> 
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,width:'100%'}}>
                        <Form.Item<FieldType>
                            label="Responsable (Empleado)"
                            name="responsibleId"
                            rules={[{ required: true, message: 'Seleccione un responsable!' }]}
                        >
                            <ContactSearchSelect 
                                role="EMPLOYEE"
                                placeholder="Seleccione un Responsable"
                                initialOptions={initialResponsible}
                                onSelectContact={(c) => handleContactsFound([c])}
                            />
                        </Form.Item>
                        <Form.Item<FieldType>
                            label="Participantes (Clientes)"
                            name="participantIds"
                        >
                            <ContactSearchSelect 
                                mode="multiple"
                                role="CUSTOMER"
                                placeholder="Seleccione Participantes"
                                initialOptions={initialParticipants}
                                onSelectContacts={handleContactsFound}
                            />
                        </Form.Item>
                    </div>

                    <div style={{paddingTop:8,display:'flex',justifyContent:'flex-end',alignItems:'center',gap:8,width:'100%'}}>
                        <Form.Item label={null} style={{marginBottom:0}}>
                            <Button type="primary" htmlType="submit">
                                Guardar
                            </Button>
                        </Form.Item>
                        <Form.Item label={null} style={{marginBottom:0}}>
                            <Popconfirm
                                title="¿Eliminar evento?"
                                description="¿Está seguro de que desea eliminar este evento?"
                                onConfirm={() => handleDelete()}
                                okText="Sí"
                                cancelText="No"
                                placement="topRight"
                            >
                                <Button danger>
                                    Eliminar
                                </Button>
                            </Popconfirm>
                        </Form.Item>  
                    </div>
                </Form>
                </>
            )
    }
}
